import React, {useCallback, useState} from 'react';
import { useQuery } from 'react-query';
import {useImmerReducer} from 'use-immer';
import {toast} from 'react-toastify';
import {t} from "i18next";

import useProjectContext from '@/services/context/project/use-project-context.ts';
import useProjectMeasureContext from '@/pages/takeoff/context/project-measure/use-project-measure-context.ts';
import useProjectIndicatorsContext from "@/pages/takeoff/context/project-indicators/use-project-indicators-context.ts";
import EXTRACTION_ENDPOINT from '@/services/endpoints/extraction-endpoints.ts';

import extractionReducer from './extraction-reducer.ts';
import {fetchBlob, fetchItem, postItem} from '@/queries/queries-helper';
import {magicDataUnity, magicDefaultDataType} from '@/utils/magic-traduction.ts';
import ArticleDto from '@/models/interfaces/extraction/article-dto.ts';
import ExtractionDataDto from '@/models/interfaces/dtos/extraction/extraction-data-dto.ts';
import ExtractionSheetLeafNodeDto from '@/models/interfaces/dtos/extraction/extraction-sheet-leaf-node-dto.ts';
import ExtractionSheetArticleNodeDto from '@/models/interfaces/dtos/extraction/extraction-sheet-article-node-dto.ts';
import ExtractionNodeType from "@/models/interfaces/dtos/extraction/extraction-node-type.ts";

import Measure from '@/pages/takeoff/context/project-measure/models/measure.ts';
import ProjectExtractionSheets from './models/project-extraction-sheets.ts';
import ExtractionOperation, {operationMap} from './models/enums/extraction-operation.ts';

import ExtractionSheetLeafNode from './models/extraction-sheet-leaf-node.ts';
import ExtractionSheetArticleNode from './models/extraction-sheet-article-node.ts';
import ExtractionSheetTree from './models/extraction-sheet-tree.ts';

import {ExtractionContext, ExtractionContextProps} from './extraction-context-props.ts';
import ExtractionReducerAction from './extraction-reducer-action.ts';
import UpsertExtractionSheetDto from "@/models/interfaces/dtos/extraction/upsert-extraction-sheet-dto.ts";
import ExtractionSheetArticleElementBase from "./models/article-elements/extraction-sheet-article-element-base.ts";
import ExtractionSheetArticleMeasuresElement
    from "@/pages/takeoff/layouts/extraction/context/models/article-elements/extraction-sheet-article-measures-element.ts";
import ExtractionSheetArticle3DElement
    from "@/pages/takeoff/layouts/extraction/context/models/article-elements/extraction-sheet-article-3D-element.ts";
import ExtractionSheetArticleElementDto, {
    ExtractionSheetArticle3DElementDto,
    ExtractionSheetArticleMeasuresElementDto
} from "@/models/interfaces/dtos/extraction/extraction-sheet-article-element-dto.ts";

const ExtractionProvider: React.FC<React.PropsWithChildren> = ({children}) => {
    
    const { project } = useProjectContext();
    const { projectMeasures, projectMeasureTypes, predefinedShapes } = useProjectMeasureContext();
    const { projectIndicatorsData, isLoading: isProjectIndicatorsLoading } = useProjectIndicatorsContext();
    
    const [isSaving, setIsSaving] = useState(false);
    
    const [data, dispatch] = useImmerReducer<ProjectExtractionSheets | null, ExtractionReducerAction>(
        extractionReducer,
        null
    );
        
    const { isLoading } = useQuery(
        ['project-extraction', project.id],
        async () => fetchItem<ExtractionDataDto[]>(EXTRACTION_ENDPOINT.GET_BAAMER_REVISION_EXTRACTION(project.baamerRevisionId)),
        {
            refetchOnWindowFocus: false,
            enabled: project.id !== undefined && !isProjectIndicatorsLoading,
            onSuccess: (response) => {
                if (response && response.length > 0) {
                    dispatch({ type: 'hydrate', payload: { dto: response, projectMeasures, measureTypes: projectMeasureTypes, projectIndicatorsData } });
                } else {
                    dispatch({type: 'createNew'});
                }
            },
            onError: () => {
                toast.error('Erreur lors de la récupérations des feuilles');
                dispatch({type: 'createNew'});
            },
        }
    );
    
    /* GESTION FEUILLES */
    
    const addSheet = useCallback((name: string) => {
        dispatch({type: 'addSheet', payload: {name}});
    }, [dispatch]);
    
    const updateSheetName = useCallback((id: string, newName: string) => {
        dispatch({type: 'updateSheetName', payload: {id, newName}});
    }, [dispatch]);
    
    const removeSheet = useCallback((id: string) => {
        dispatch({type: 'removeSheet', payload: {id}});
    }, [dispatch]);
    
    /* GESTION NOEUDS */
    
    const addNode = useCallback((sheetId: string, nodeName: string) => {
        dispatch({type: 'addNode', payload: {sheetId, nodeName}});
    }, [dispatch]);
    
    const updateNodeName = useCallback((sheetId: string, nodeId: string, newName: string) => {
        dispatch({type: 'updateNodeName', payload: {sheetId, nodeId, newName}});
    }, [dispatch]);
 
    const addSubNode = useCallback((sheetId: string, nodeId: string, subNodeName: string) => {
        dispatch({type: 'addSubNode', payload: {sheetId, nodeId, subNodeName}});
    }, [dispatch]);

    const removeNode = useCallback((sheetId: string, nodeId: string) => {
        dispatch({type: 'removeNode', payload: {sheetId, nodeId}});
    }, [dispatch]);
    
    /* GESTION ARTICLES */
    
    const addArticle = useCallback((sheetId: string, nodeId: string, articleName: string) => {
        dispatch({type: 'addArticle', payload: {sheetId, nodeId, articleName}});
    }, [dispatch]);
    
    const updateArticleName = useCallback((sheetId: string, articleId: string, articleName: string) => {
        dispatch({type: 'updateArticleName', payload: {sheetId, articleId, articleName}});
    }, [dispatch]);
    
    const removeArticle = useCallback((sheetId: string, articleId: string) => {
        dispatch({type: 'removeArticle', payload: {sheetId, articleId}});
    }, [dispatch]);
    
    /* GESTION ELEMENT D'ARTICLE */

    const linkMeasuresToArticle = useCallback((sheetId: string, articleId: string, measureTypeId: string, associatedStoreysIds: string[]) => {
        const measuresToLink: Measure[] = [];
        for (let i = 0; i < associatedStoreysIds.length; i += 1) {
            const storeyId = associatedStoreysIds[i];
            const storey = projectMeasures.getStorey(storeyId);
            if(storey == null)
                break;

            measuresToLink.push(...storey.measures.filter(m => m.measureTypeId === measureTypeId));
        }

        const measureType = projectMeasureTypes.find(m => m.measureTypeId === measureTypeId);
        if(measureType == null) return;
        const shape = predefinedShapes.find(s => s.measureShapeId === measureType.measureShapeId)?.name;
        const defaultType = magicDefaultDataType(shape);
        const defaultUnity = magicDataUnity(defaultType);

        dispatch({type: 'linkMeasuresToArticle', payload: {sheetId, articleId, measureTypeId, measureTypeName: measureType.name, associatedStoreysIds, measures: measuresToLink, defaultType, defaultUnity}});
    }, [dispatch, predefinedShapes, projectMeasureTypes, projectMeasures]);
    
    const link3DToArticle = useCallback((sheetId: string, articleId: string, indicatorDefKeyName: string, associatedStoreysIds: number[]) => {
        const storeys = projectIndicatorsData?.buildings.flatMap(b => b.storeys).filter(s => associatedStoreysIds.includes(s.storeyId));
        const indicators = storeys?.flatMap(s => s.indicators).filter(i => i.def.keyName === indicatorDefKeyName);
        if(indicators == null) return;
        
        dispatch({type: 'link3DToArticle', payload: {sheetId, articleId, indicatorDefKeyName, associatedStoreysIds, indicators}});
    }, [dispatch, projectIndicatorsData?.buildings]);

    const updateArticleData = useCallback((sheetId: string, articleElementId: string, dataKey: string, operation: ExtractionOperation) => {
        dispatch({type: 'updateArticleData', payload: {sheetId, articleElementId, dataKey, operation}});
    }, [dispatch]);
    
    const saveSheets = useCallback(async () => {
        if(data == null || data.sheets.length === 0) return;
        
        try {
            setIsSaving(true);
            
            const mapLeafNode = (node: ExtractionSheetLeafNode): ExtractionSheetLeafNodeDto => ({
                type: ExtractionNodeType.LEAF,
                name: node.name,
                children: node.children.map(child => {
                    if (child instanceof ExtractionSheetLeafNode) {
                        return mapLeafNode(child);
                    }
                    if (child instanceof ExtractionSheetArticleNode) {
                        const articleNode: ExtractionSheetArticleNodeDto = {
                            type: ExtractionNodeType.ARTICLE,
                            name: child.name,
                            children: child.children.map((element: ExtractionSheetArticleElementBase) => {
                                const base = {
                                    type: element.type,
                                    dataKey: element.dataKey,
                                    operation: operationMap[element.operation].label,
                                };

                                if (element instanceof ExtractionSheetArticleMeasuresElement) {
                                    return {
                                        ...base,
                                        associatedMeasureTypeId: element.associatedMeasureTypeId,
                                        associatedStoreysIds: element.associatedStoreysIds,
                                    } as ExtractionSheetArticleMeasuresElementDto;
                                }

                                if (element instanceof ExtractionSheetArticle3DElement) {
                                    return {
                                        ...base,
                                        associatedStoreysIds: element.associatedStoreysIds,
                                    } as ExtractionSheetArticle3DElementDto;
                                }

                                return base as ExtractionSheetArticleElementDto;
                            })
                        };
                        return articleNode;
                    }
                    throw new Error('Unknown node type');
                })
            });

            const mapSheet = (sheet: ExtractionSheetTree): ExtractionDataDto => ({
                type: ExtractionNodeType.TREE,
                name: sheet.name,
                children: sheet.children.map(mapLeafNode)
            });

            const payload: UpsertExtractionSheetDto = {
                data: JSON.stringify(data.sheets.map(mapSheet))
            };

            await postItem(EXTRACTION_ENDPOINT.SAVE_BAAMER_REVISION_EXTRACTION(project.baamerRevisionId), payload);
            
            dispatch({type: 'setIsDirty', payload: {dirty: false}});
            toast.success('Sauvegarde réussie');
        } catch (error) {
            if(error instanceof Error)
                toast.error(error.message);
        } finally {
            setIsSaving(false);
        }
    }, [data, project.baamerRevisionId, dispatch]);
    
    const exportExcel = useCallback(async (sheetId: string) => {
        try {
            if(data == null) return;
            const sheet = data.sheets.find(s => s.id === sheetId);
            const elements = sheet?.getNodeArticlesElements();
            if(sheet == null || elements == null) return;

            const blobData: ArticleDto[] = elements.map((element) => {
                const article = data.getArticleById(element.parentArticleId);
                
                let type; let attr;
                if(element instanceof ExtractionSheetArticleMeasuresElement) {
                    type = element.associatedMeasureTypeName;
                    attr = t(element.dataKey);
                } else if (element instanceof ExtractionSheetArticle3DElement){
                    type = element.associatedIndicators[0].def.keyName;
                    attr = "Valeur";
                } else {
                    type = element.type;
                    attr = "Valeur";
                }
                
                return {
                    name: article?.name ?? 'Article',
                    type: type,
                    attr: attr,
                    count: element.elements,
                    value: element.value,
                    unity: element.unity,
                    operationType: operationMap[element.operation].label
                }
            });
            
            const excelBlob = await fetchBlob(EXTRACTION_ENDPOINT.EXPORT_EXCEL(), blobData);
            if(excelBlob == null) {
                toast.error('Erreur lors de l\'export Excel');
                return;
            }

            // Téléchargement du fichier par le navigateur
            const downloadUrl = URL.createObjectURL(excelBlob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `${project.name} - ${sheet.name} - Extraction.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            if(error instanceof Error)
                toast.error(error.message);
        }
    }, [data, project.name]);

    // Context props
    const contextProps: ExtractionContextProps = {
        isLoading,
        projectSheets: data,

        isDirty: data?.isDirty ?? false,
        isSaving,
        saveSheets,
        
        addSheet,
        updateSheetName,
        removeSheet,

        addNode,
        updateNodeName,
        addSubNode,
        removeNode,
        
        addArticle,
        updateArticleName,
        linkMeasuresToArticle,
        link3DToArticle,
        updateArticleData,
        removeArticle,

        exportExcel,
    };
    
    return (
        <ExtractionContext.Provider value={contextProps}>
            {children}
        </ExtractionContext.Provider>
    );
};

export default ExtractionProvider;